import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiX, FiLink, FiSend } from "react-icons/fi";
import { handleImagePaste } from "../../utils/imagePasteHelper";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const REDIRECT_ROUTES = [
  "/ANNOUNCEMENT", "/home", "/chat", "/refer", "/profile", "/settings",
  "/leaderboard", "/creator-hub", "/creator-profile", "/content-info",
  "/information", "/edit-profile", "/kyc-verification", "/kyc-help",
  "/login", "/signup", "/verification", "/forgot-password", "/reset-password",
  "/change-password", "/strike", "/thankyou", "/update-app",
];

const SendNotificationModal = ({ isOpen, onClose, userId }) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [redirectRoute, setRedirectRoute] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const imageInputRef = useRef(null);

  if (!isOpen) return null;

  const handleImageChange = (e) => {
    const file = e.target.files[0] || null;
    setImage(file);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const handlePaste = async (e) => {
    try {
      const file = await handleImagePaste(e, ".jpg,.jpeg,.png,.webp");
      if (file) {
        setImage(file);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(URL.createObjectURL(file));
        if (imageInputRef.current) {
          // Clear file input so it doesn't conflict
          imageInputRef.current.value = "";
        }
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCancel = () => {
    setTitle("");
    setMessage("");
    setImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setRedirectRoute("");
    if (imageInputRef.current) imageInputRef.current.value = "";
    onClose();
  };

  const handleSubmit = async () => {
    if (!title || !message) {
      toast.error("Title and message are required");
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem("token");

    const fd = new FormData();
    fd.append("userId", userId);
    fd.append("title", title);
    fd.append("message", message);
    if (image) fd.append("image", image);
    if (redirectRoute) fd.append("redirectRoute", redirectRoute);

    try {
      const res = await fetch(`${baseUrl}/notification/send-to-user`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const result = await res.json();
      const isSuccess = res.ok && result.status !== false && !result.error;
      if (isSuccess) {
        toast.success("Notification sent successfully!");
        handleCancel(); // resets form and closes modal
      } else {
        toast.error(result.message || result.error || "Failed to send notification");
      }
    } catch {
      toast.error("Server error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Send Notification</h3>
            <p className="text-sm text-gray-500">Target User ID: {userId}</p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition p-1"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="Notification title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              placeholder="Notification message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div 
              onPaste={handlePaste} 
              tabIndex={0}
              className="border-2 border-dashed border-gray-200 rounded-xl p-4 focus:outline-none focus:border-blue-400 focus:bg-blue-50/50 hover:bg-gray-50/50 transition-all"
            >
              <label className="block text-xs font-medium text-gray-500 mb-2">
                Banner Image <span className="text-gray-300">(optional)</span>
              </label>
              <input
                ref={imageInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleImageChange}
                className="w-full text-sm text-gray-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 cursor-pointer"
              />
              <p className="text-[11px] text-gray-400 mt-2">
                Click this area and press <strong>Ctrl + V</strong> to paste an image.
              </p>
              {imagePreview && (
                <div className="mt-2 relative w-fit">
                  <img src={imagePreview} alt="preview" className="h-20 rounded-lg object-cover border" />
                  <button
                    onClick={() => {
                      setImage(null);
                      if (imagePreview) URL.revokeObjectURL(imagePreview);
                      setImagePreview(null);
                      if (imageInputRef.current) imageInputRef.current.value = "";
                    }}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow-sm"
                  >
                    <FiX size={10} />
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center">
                <FiLink className="inline mr-1" size={11} />
                Redirect Route <span className="text-gray-300 ml-1">(optional)</span>
              </label>
              <select
                value={redirectRoute}
                onChange={(e) => setRedirectRoute(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">-- Select route --</option>
                {REDIRECT_ROUTES.map((route) => (
                  <option key={route} value={route}>{route}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            onClick={handleCancel}
            disabled={submitting}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition shadow-sm"
          >
            {submitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiSend size={15} />
            )}
            {submitting ? "Sending..." : "Send Notification"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendNotificationModal;
